import type { Inertia, Joint, Site } from "../../types/scene";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import { logger } from "../../utils/logger";
import { isScalarJoint } from "./xmlGeneratorConfig";
import { emitBodySitesXml, emitMountedCamerasXml } from "./xmlGeneratorCameraSite";
import { emitMountedLightsXml } from "./xmlGeneratorLight";
import {
  arrayToString,
  colorToRgba,
  formatMjcfNumber,
  normalizeQuat,
  quatConjugate,
  quatMultiply,
  quatRotateVec,
  toMjcfQuatWxyz,
  viewerQuatToMujoco,
  viewerVecToMujoco,
  xmlEscapeAttr,
} from "./xmlGenerationHelpers";
import type {
  ExportBodyNode,
  ExportCamera,
  ExportGeom,
  ExportLight,
  GeomWithInertiaHints,
  MeshMetadataForExport,
} from "./xmlGenerationTypes";

type StartupXmlFlagsForBodyExport = {
  emitScalarJointRef: boolean;
  seedScalarInitQposFromCurrent: boolean;
};

type EmitBodyTreeXmlOptions = {
  bodyTreeForExport: ExportBodyNode[];
  bodyUuidToXmlName: Record<string, string>;
  bodyChildclassDefaultNames: Set<string>;
  addExportWarning: (message: string) => void;
  emitInertials: boolean;
  defaultInertial: Omit<Inertia, "uuid" | "name">;
  jointMap: Record<string, Joint>;
  jointUuidToXmlName: Record<string, string>;
  jointXmlNameMap: Record<string, string>;
  emittedJointNames: string[];
  initScalarQposByJointUuid: Record<string, number>;
  startupXmlFlags: StartupXmlFlagsForBodyExport;
  geomDefaultClassNames: Set<string>;
  geomUuidToXmlName: Record<string, string>;
  spatialReferencedGeomNames: Set<string>;
  materialUuidToName: Record<string, string>;
  materialNameSet: Set<string>;
  meshUuidToXmlName: Record<string, string>;
  unsupportedMeshUuidsForMjcf: Set<string>;
  hfieldNameToXmlName: Record<string, string>;
  meshDefinitions: Record<string, MeshMetadataForExport | null | undefined>;
  siteExportNameByUuid: Record<string, string>;
  mountedCamerasByBodyUuid: Record<string, ExportCamera[]>;
  cameraExportNameByUuid: Record<string, string>;
  mountedLightsByBodyUuid: Record<string, ExportLight[]>;
  lightExportNameByUuid: Record<string, string>;
  getLiveFreeJointBodyPose: (
    body: ExportBodyNode
  ) => { position: number[]; orientation: number[] } | null;
  findSiteByIdentifier: (identifier: string | undefined | null) => Site | null;
  droppedBodyQuatWarnings: string[];
};

export const emitBodyTreeXml = ({
  bodyTreeForExport,
  bodyUuidToXmlName,
  bodyChildclassDefaultNames,
  addExportWarning,
  emitInertials,
  defaultInertial,
  jointMap,
  jointUuidToXmlName,
  jointXmlNameMap,
  emittedJointNames,
  initScalarQposByJointUuid,
  startupXmlFlags,
  geomDefaultClassNames,
  geomUuidToXmlName,
  spatialReferencedGeomNames,
  materialUuidToName,
  materialNameSet,
  meshUuidToXmlName,
  unsupportedMeshUuidsForMjcf,
  hfieldNameToXmlName,
  meshDefinitions,
  siteExportNameByUuid,
  mountedCamerasByBodyUuid,
  cameraExportNameByUuid,
  mountedLightsByBodyUuid,
  lightExportNameByUuid,
  getLiveFreeJointBodyPose,
  findSiteByIdentifier,
  droppedBodyQuatWarnings,
}: EmitBodyTreeXmlOptions): {
  worldbodyBodyXml: string;
  generateBodyXml: (body: ExportBodyNode, indent?: string) => string;
} => {
  const DEFAULT_INERTIAL = defaultInertial;
  const EMIT_INERTIALS = emitInertials;

  const cameraSiteXmlHelpers = {
    arrayToString,
    colorToRgba,
    findSiteByIdentifier,
    normalizeQuat,
    quatMultiply,
    quatRotateVec,
    sanitizeMjcfName,
    toMjcfQuatWxyz,
    viewerQuatToMujoco,
    viewerVecToMujoco,
    xmlEscapeAttr,
  };

  const generateBodyXml = (body: ExportBodyNode, indent = "    "): string => {
    // Debug: Log body hierarchy for problematic bodies
    if (body.name === "link3" || body.name === "link4") {
      logger.debug(`[XML Gen] Generating body ${body.name} at indent level ${indent.length / 2}`);
    }

    const xmlBodyName =
      (body?.uuid && bodyUuidToXmlName[String(body.uuid)]) || sanitizeMjcfName(body.name);
    let bodyPosForExport =
      Array.isArray(body.position) && body.position.length === 3
        ? body.position.map((value) => Number(value) || 0)
        : [0, 0, 0];
    let bodyQuatForExport =
      Array.isArray(body.orientation) && body.orientation.length === 4
        ? normalizeQuat(body.orientation)
        : [1, 0, 0, 0];
    const liveFreeJointPose = getLiveFreeJointBodyPose(body);
    if (liveFreeJointPose) {
      bodyPosForExport = liveFreeJointPose.position;
      bodyQuatForExport = liveFreeJointPose.orientation;
    }

    let xml = `${indent}<body name="${xmlBodyName}"`;

    if (bodyPosForExport.some((value) => value !== 0)) {
      xml += ` pos="${arrayToString(bodyPosForExport)}"`;
    }

    // Debug: Log body hierarchy for problematic bodies
    if (body.name === "link3" || body.name === "link4") {
      logger.debug(
        `[XML Gen] Body ${body.name}: pos=${body.position ? arrayToString(body.position) : "none"}`
      );
    }

    const rawQuat = bodyQuatForExport;
    let emitQuat: number[] | null = null;

    if (rawQuat != null) {
      if (Array.isArray(rawQuat) && rawQuat.length === 4) {
        const normalized = toMjcfQuatWxyz(rawQuat);
        const identity: [number, number, number, number] = [1, 0, 0, 0];
        const isIdentity = normalized.every(
          (value, index) => Math.abs(value - identity[index]) < 1e-6
        );
        if (!isIdentity) {
          emitQuat = normalized;
        }
      } else {
        const warning = `${body.name ?? "(unnamed)"} (invalid quaternion)`;
        if (!droppedBodyQuatWarnings.includes(warning)) {
          droppedBodyQuatWarnings.push(warning);
        }
      }
    }

    if (emitQuat) {
      xml += ` quat="${arrayToString(emitQuat)}"`;
    }

    if (
      body.gravityCompensation &&
      body.gravityCompensation !== 0 &&
      Number.isFinite(body.gravityCompensation)
    ) {
      xml += ` gravcomp="${formatMjcfNumber(body.gravityCompensation)}"`;
    }
    if (typeof body.isMocap === "boolean") {
      xml += ` mocap="${body.isMocap ? "true" : "false"}"`;
    }
    if (typeof body.childclass === "string" && body.childclass.trim().length > 0) {
      const childclass = body.childclass.trim();
      if (bodyChildclassDefaultNames.has(childclass)) {
        xml += ` childclass="${xmlEscapeAttr(childclass)}"`;
      } else {
        addExportWarning(
          `Omitted body childclass "${childclass}" on "${body.name ?? "(unnamed)"}" because the generated XML has no matching default class.`
        );
      }
    }
    if (typeof body.user === "string" && body.user.trim().length > 0) {
      xml += ` user="${xmlEscapeAttr(body.user.trim())}"`;
    }

    xml += ">\n";

    if (Array.isArray(body.plugins)) {
      body.plugins.forEach((pluginDef) => {
        if (typeof pluginDef === "string") {
          const pluginXml = pluginDef.trim();
          if (!pluginXml) return;
          xml += `${indent}  ${pluginXml}\n`;
          return;
        }
        if (!pluginDef || typeof pluginDef !== "object") return;
        // Backward compatibility: export legacy attribute-only plugin definitions.
        const entries = Object.entries(pluginDef)
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
        if (entries.length === 0) return;
        let pluginLine = `${indent}  <plugin`;
        entries.forEach(([key, value]) => {
          pluginLine += ` ${key}="${xmlEscapeAttr(String(value))}"`;
        });
        pluginLine += "/>\n";
        xml += pluginLine;
      });
    }

    // If geoms provide inferable inertia, MuJoCo can derive inertials from geoms.
    // In that case, do not inject a fallback inertial at the body origin because it can pin the COM
    // on a hinge axis (e.g. articulated props like books) and remove expected gravity torque.
    const hasGeomWithMassOrDensity = body.geoms?.some((geom: GeomWithInertiaHints) => {
      if (!geom) return false;
      const mass = Number(geom.mass);
      if (Number.isFinite(mass) && mass > 0) return true;
      const density = Number(geom.density);
      if (Number.isFinite(density) && density > 0) return true;
      return false;
    });
    const hasInferablePrimitiveGeom = body.geoms?.some((geom: GeomWithInertiaHints) => {
      if (!geom) return false;
      if (String(geom.kind || "").toLowerCase() === "mesh") return false;
      if (!Array.isArray(geom.size) || geom.size.length < 1) return false;
      const sizeValues = geom.size.map((value: unknown) => Number(value));
      return sizeValues.some((value: number) => Number.isFinite(value) && value > 0);
    });
    const hasInferableGeomInertia = !!hasGeomWithMassOrDensity || !!hasInferablePrimitiveGeom;
    const explicitInertial = body.inertia ?? null;

    if (EMIT_INERTIALS && (explicitInertial || !hasInferableGeomInertia)) {
      const inertial = explicitInertial ?? DEFAULT_INERTIAL;
      const rawMass = Number(inertial.mass);
      const inertialMass =
        Number.isFinite(rawMass) && rawMass > 0 ? rawMass : DEFAULT_INERTIAL.mass;
      xml += `${indent}  <inertial mass="${formatMjcfNumber(inertialMass)}"`;
      const posValues =
        Array.isArray(inertial.position) && inertial.position.length === 3
          ? inertial.position.map((value) => Number(value))
          : [...DEFAULT_INERTIAL.position];
      xml += ` pos="${arrayToString(posValues)}"`;
      if (inertial.orientation) {
        const normalized = normalizeQuat(inertial.orientation);
        if (
          !(
            normalized[0] === 1 &&
            normalized[1] === 0 &&
            normalized[2] === 0 &&
            normalized[3] === 0
          )
        ) {
          xml += ` quat="${arrayToString(normalized)}"`;
        }
      }
      if (Array.isArray(inertial.fullinertia) && inertial.fullinertia.length === 6) {
        const fi = inertial.fullinertia.map(Number);
        if (fi.every(Number.isFinite) && fi[0] > 0 && fi[1] > 0 && fi[2] > 0) {
          xml += ` fullinertia="${arrayToString(fi)}"`;
        } else {
          logger.warn(
            `[XML Gen] Invalid fullinertia for body "${body.name}" (diagonal must be positive) ? using default diaginertia.`
          );
          xml += ` diaginertia="${arrayToString(DEFAULT_INERTIAL.diagonal)}"`;
        }
      } else {
        const rawDiag = Array.isArray(inertial.diagonal) ? inertial.diagonal.map(Number) : null;
        const diagonal =
          rawDiag &&
          rawDiag.length === 3 &&
          rawDiag.every((v: number) => Number.isFinite(v) && v > 0)
            ? rawDiag
            : DEFAULT_INERTIAL.diagonal;
        if (diagonal !== rawDiag) {
          logger.warn(
            `[XML Gen] Invalid diaginertia for body "${body.name}" (must be 3 finite positive values) ? using default.`
          );
        }
        xml += ` diaginertia="${arrayToString(diagonal)}"`;
      }
      xml += "/>\n";
    }

    // Resolve the first joint from body.joints instead of searching by joint.body
    const resolveJointRef = (entry: unknown): Joint | null => {
      if (!entry) return null;
      if (typeof entry === "string") {
        // entry must be a UUID (jointMap keys are UUIDs)
        const joint = jointMap[entry as import("../../types/scene").JointUUID];
        if (!joint) {
          logger.error(
            `[resolveJointRef] Joint UUID "${entry}" not found in jointMap. body.joints must contain UUIDs, not names.`
          );
          return null;
        }
        return joint;
      }
      // If it's an object, check if we need to look it up in the map
      if (typeof entry !== "object") return null;
      const jointRef = entry as Partial<Joint>;
      const baseIdentifier = jointRef.uuid ?? jointRef.name;
      if (baseIdentifier) {
        // Try UUID first (direct key lookup)
        const foundByUuid =
          baseIdentifier && jointMap[baseIdentifier as import("../../types/scene").JointUUID];
        if (foundByUuid) {
          // Prefer canonical map values so live joint state wins over stale embedded refs.
          return { ...jointRef, ...foundByUuid } as Joint;
        }
        // Fallback: search by name
        const found = Object.values(jointMap).find(
          (j) => j && (j.uuid === baseIdentifier || j.name === baseIdentifier)
        );
        return found ? ({ ...jointRef, ...found } as Joint) : (jointRef as Joint);
      }
      return jointRef as Joint;
    };

    // Process all joints on this body (not just the first one)
    // This is important for free-floating objects where the free joint must be emitted
    const bodyJointRefs = Array.isArray(body.joints) ? body.joints : [];
    const resolvedBodyJoints = bodyJointRefs
      .map((ref) => resolveJointRef(ref))
      .filter((j) => j !== null);

    const rememberJointXmlName = (bodyJoint: Joint, uniqueName: string): void => {
      const originalJointName = sanitizeMjcfName(bodyJoint?.name || "");
      if (bodyJoint?.uuid) {
        jointXmlNameMap[bodyJoint.uuid] = uniqueName;
      }
      jointXmlNameMap[`${body.uuid}|${bodyJoint?.uuid}`] = uniqueName;
      jointXmlNameMap[`${body.uuid}|${bodyJoint?.name}`] = uniqueName;
      jointXmlNameMap[`${body.uuid}|${originalJointName}`] = uniqueName;
      if (!jointXmlNameMap[originalJointName]) {
        jointXmlNameMap[originalJointName] = uniqueName;
      }
      const sanitizedJoint = sanitizeMjcfName(bodyJoint?.name || "");
      if (!jointXmlNameMap[sanitizedJoint]) {
        jointXmlNameMap[sanitizedJoint] = uniqueName;
      }
      const sourceFreeJointName =
        bodyJoint?.kind === "free" && typeof bodyJoint?.mjcfName === "string"
          ? sanitizeMjcfName(bodyJoint.mjcfName)
          : "";
      if (sourceFreeJointName && !jointXmlNameMap[sourceFreeJointName]) {
        jointXmlNameMap[sourceFreeJointName] = uniqueName;
      }
    };

    // Check whether a joint is a free joint (must be emitted first as <freejoint/>)
    const freeJoint = resolvedBodyJoints.find((j) => j.kind === "free");
    if (freeJoint) {
      let freejointLine = `${indent}  <freejoint`;
      const explicitName = typeof freeJoint.mjcfName === "string" ? freeJoint.mjcfName.trim() : "";
      const uniqueName =
        (freeJoint.uuid && jointUuidToXmlName[String(freeJoint.uuid)]) ||
        sanitizeMjcfName(explicitName || `${body.uuid}_freejoint`);
      const align = typeof freeJoint.align === "string" ? freeJoint.align.trim() : "";
      rememberJointXmlName(freeJoint, uniqueName);
      if (explicitName) freejointLine += ` name="${xmlEscapeAttr(uniqueName)}"`;
      if (align) freejointLine += ` align="${xmlEscapeAttr(align)}"`;
      freejointLine += "/>\n";
      xml += freejointLine;
    }

    // Emit all non-free joints
    for (const bodyJoint of resolvedBodyJoints) {
      if (bodyJoint.kind === "free") {
        // Already emitted as <freejoint/>, skip
        continue;
      }

      const originalJointName = sanitizeMjcfName(bodyJoint.name);
      const jointType = bodyJoint.kind && bodyJoint.kind.trim() !== "" ? bodyJoint.kind : "hinge";

      const uniqueName =
        (bodyJoint?.uuid && jointUuidToXmlName[String(bodyJoint.uuid)]) ||
        sanitizeMjcfName(`${body.uuid}_${originalJointName}`);

      // Record mapping for actuators and keyframes
      rememberJointXmlName(bodyJoint, uniqueName);

      xml += `${indent}  <joint name="${uniqueName}"`;
      xml += ` type="${jointType}"`;
      // Axis exists only on HingeJoint and SlideJoint
      if (isScalarJoint(bodyJoint)) {
        if (bodyJoint.axis && Array.isArray(bodyJoint.axis) && bodyJoint.axis.length === 3) {
          if (bodyJoint.name.includes("joint3") || bodyJoint.name.includes("joint4")) {
            logger.debug(
              `[XML Gen] ${bodyJoint.name}: axis=${arrayToString(bodyJoint.axis)}, body=${body.name}, body_pos=${body.position ? arrayToString(body.position) : "none"}, body_quat=${rawQuat ? arrayToString(rawQuat) : "none"}`
            );
          }
          const axisValues = bodyJoint.axis.map(Number);
          const axisLengthSq = axisValues.reduce((sum: number, v: number) => sum + v * v, 0);
          if (axisValues.every(Number.isFinite) && axisLengthSq > 1e-12) {
            xml += ` axis="${arrayToString(axisValues)}"`;
          } else {
            logger.warn(
              `[XML Gen] Skipping zero/invalid axis for joint "${bodyJoint.name}" ? MuJoCo will use its default axis.`
            );
          }
        }
      }
      // Emit joint local pivot position if set (exists on BallJoint, HingeJoint, SlideJoint)
      if (bodyJoint.kind === "ball" || isScalarJoint(bodyJoint)) {
        const position = bodyJoint.kind === "ball" ? bodyJoint.position : bodyJoint.position;
        if (position && Array.isArray(position) && position.length === 3) {
          const p = position as number[];
          if (p.some((v) => Math.abs(Number(v)) > 1e-12)) {
            xml += ` pos="${arrayToString(p.map(Number))}"`;
          }
        }
      }
      // Runtime startup is driven by the explicit init keyframe plus seeded actuator controls.
      // Keep qpos0/reference offsets from the loaded MJCF when available; do not bake the
      // current scene pose into refs, especially for body-connect closed-loop mechanisms.
      if (startupXmlFlags.emitScalarJointRef && isScalarJoint(bodyJoint)) {
        const refFromInitial = Number(bodyJoint.initialRef);
        const refFromInit = startupXmlFlags.seedScalarInitQposFromCurrent
          ? undefined
          : initScalarQposByJointUuid[String(bodyJoint.uuid)];
        const refFromCurrent = startupXmlFlags.seedScalarInitQposFromCurrent
          ? undefined
          : Number(bodyJoint.ref);
        const ref = Number.isFinite(refFromInitial)
          ? refFromInitial
          : Number.isFinite(Number(refFromInit))
            ? Number(refFromInit)
            : refFromCurrent;
        const refValue = Number(ref);
        if (Number.isFinite(refValue) && Math.abs(refValue) > 1e-12) {
          xml += ` ref="${formatMjcfNumber(refValue)}"`;
        }
      }
      // Range exists on HingeJoint and SlideJoint.
      // Note: compiler has autolimits="true", so we must NOT emit limited="true" ?
      // MuJoCo 2.2+ throws mjXError if both are present.
      if (isScalarJoint(bodyJoint) && bodyJoint.range) {
        // Skip emitting range when it's exactly [-p, p] ? that's the parser's sentinel
        // for "no explicit range in the source XML". Emitting it with autolimits="true"
        // creates a full-circle limit constraint that the Euler/RK4 integrators reject.
        const [rLo, rHi] = bodyJoint.range as [number, number];
        if (!Number.isFinite(rLo) || !Number.isFinite(rHi)) {
          logger.warn(
            `[xmlGenerationActions] Joint "${uniqueName}" range [${rLo}, ${rHi}] contains non-finite values; attribute omitted.`
          );
        } else {
          let lo = Number(rLo);
          let hi = Number(rHi);
          if (lo > hi) {
            logger.warn(
              `[xmlGenerationActions] Joint "${uniqueName}" range [${lo}, ${hi}] had min > max; swapping for MuJoCo export.`
            );
            const t = lo;
            lo = hi;
            hi = t;
          }
          const isDefaultPiRange = Math.abs(lo + Math.PI) < 1e-9 && Math.abs(hi - Math.PI) < 1e-9;
          if (!isDefaultPiRange) {
            xml += ` range="${arrayToString([lo, hi])}"`;
          }
        }
      } else if (bodyJoint.kind === "ball") {
        // Do NOT emit range for ball joints. With autolimits="true", a range attribute
        // creates angular limit constraints that the MuJoCo WASM build does not handle
        // correctly and causes "memory access out of bounds" during mj_step.
      }
      // Under compiler autolimits="true", a range alone enables limits. Sources such as
      // the conveyor belt use range for wrap period metadata with limited="false".
      if (isScalarJoint(bodyJoint) && bodyJoint.useLimits === "false") {
        xml += ` limited="false"`;
      }
      // Physics properties - critical for smooth motion in closed-loop mechanisms
      if (
        bodyJoint.armature !== undefined &&
        bodyJoint.armature !== 0 &&
        Number.isFinite(bodyJoint.armature)
      ) {
        xml += ` armature="${formatMjcfNumber(bodyJoint.armature)}"`;
      }
      if (
        bodyJoint.damping !== undefined &&
        bodyJoint.damping !== 0 &&
        Number.isFinite(bodyJoint.damping)
      ) {
        xml += ` damping="${formatMjcfNumber(bodyJoint.damping)}"`;
      }
      if (
        bodyJoint.stiffness !== undefined &&
        bodyJoint.stiffness !== 0 &&
        Number.isFinite(bodyJoint.stiffness)
      ) {
        xml += ` stiffness="${formatMjcfNumber(bodyJoint.stiffness)}"`;
      }
      if (
        isScalarJoint(bodyJoint) &&
        bodyJoint.restingPosition !== undefined &&
        bodyJoint.restingPosition !== 0 &&
        Number.isFinite(bodyJoint.restingPosition)
      ) {
        xml += ` springref="${formatMjcfNumber(bodyJoint.restingPosition)}"`;
      }
      if (
        bodyJoint.frictionloss !== undefined &&
        bodyJoint.frictionloss !== 0 &&
        Number.isFinite(bodyJoint.frictionloss)
      ) {
        xml += ` frictionloss="${formatMjcfNumber(bodyJoint.frictionloss)}"`;
      }
      if (typeof bodyJoint.user === "string" && bodyJoint.user.trim().length > 0) {
        xml += ` user="${xmlEscapeAttr(bodyJoint.user.trim())}"`;
      }
      // Solver parameters for joint limits (important for constraint stability)
      if (
        bodyJoint.limitSolverReference &&
        Array.isArray(bodyJoint.limitSolverReference) &&
        bodyJoint.limitSolverReference.length === 2
      ) {
        const solref = bodyJoint.limitSolverReference;
        if (!solref.every(Number.isFinite)) {
          logger.warn(
            `[xmlGenerationActions] Joint "${uniqueName}" solreflimit contains non-finite values; attribute omitted.`
          );
        } else if (solref[0] !== 0.02 || solref[1] !== 1) {
          xml += ` solreflimit="${arrayToString(solref)}"`;
        }
      }
      if (
        bodyJoint.limitSolverImpedance &&
        Array.isArray(bodyJoint.limitSolverImpedance) &&
        bodyJoint.limitSolverImpedance.length >= 3
      ) {
        const solimp = bodyJoint.limitSolverImpedance;
        if (!solimp.every(Number.isFinite)) {
          logger.warn(
            `[xmlGenerationActions] Joint "${uniqueName}" solimplimit contains non-finite values; attribute omitted.`
          );
        } else if (solimp[0] !== 0.9 || solimp[1] !== 0.95 || solimp[2] !== 0.001) {
          xml += ` solimplimit="${arrayToString(solimp)}"`;
        }
      }
      xml += "/>\n";
      emittedJointNames.push(uniqueName);
    }

    body.geoms?.forEach((geom) => {
      const exportGeom: ExportGeom = geom;
      const isMesh = !!geom.mesh;
      const meshUuid = isMesh && geom.mesh ? String(geom.mesh) : "";
      if (meshUuid && unsupportedMeshUuidsForMjcf.has(meshUuid)) {
        const bodyLabel = body?.name ? String(body.name) : String(body?.uuid || "(unnamed)");
        const geomLabel =
          geom?.name && String(geom.name).trim().length > 0
            ? String(geom.name)
            : String(geom?.uuid || "mesh geom");
        addExportWarning(
          `Omitted mesh geom "${geomLabel}" on body "${bodyLabel}" from MJCF because MuJoCo cannot load its mesh file format or complexity. The geom remains available in the editor scene.`
        );
        return;
      }
      const geomKind = geom.kind || "sphere";
      const typeAttr = isMesh ? "mesh" : geomKind;
      const sourceClass =
        typeof exportGeom.mjcfClass === "string" && exportGeom.mjcfClass.trim()
          ? exportGeom.mjcfClass.trim()
          : "";
      const roleClass = geom.visualOnly === true ? "visual" : "collision";
      let cls;
      if (sourceClass && geomDefaultClassNames.has(sourceClass)) {
        cls = sourceClass;
      } else {
        if (sourceClass) {
          addExportWarning(
            `Omitted geom class "${sourceClass}" on "${geom.name ?? "unnamed geom"}" because the generated XML has no matching default class.`
          );
        }
        cls = geomDefaultClassNames.has(roleClass) ? roleClass : "";
      }
      xml += `${indent}  <geom${cls ? ` class="${xmlEscapeAttr(cls)}"` : ""} type="${typeAttr}"`;
      const geomUuid = geom?.uuid ? String(geom.uuid) : "";
      const contactPairGeomName = geomUuid ? geomUuidToXmlName[geomUuid] : "";
      const geomExportName = geom.name ? sanitizeMjcfName(String(geom.name)) : "";
      if (contactPairGeomName) {
        xml += ` name="${xmlEscapeAttr(contactPairGeomName)}"`;
      } else if (geomExportName && spatialReferencedGeomNames.has(geomExportName)) {
        xml += ` name="${xmlEscapeAttr(geomExportName)}"`;
      }
      if (exportGeom.group !== undefined && exportGeom.group !== null) {
        const group = Number(exportGeom.group);
        if (Number.isFinite(group) && Number.isInteger(group)) {
          xml += ` group="${group}"`;
        }
      }

      // Export material assignment (MuJoCo uses material *names*; store uses material UUIDs).
      // If geom.materials contains a UUID, map to the material's name. If it already contains a name, pass through.
      const matSlot =
        Array.isArray(geom.materials) && geom.materials.length ? geom.materials[0] : null;
      if (matSlot != null) {
        const key = String(matSlot);
        const resolvedName = materialUuidToName[key] || (materialNameSet.has(key) ? key : null);
        if (resolvedName) {
          xml += ` material="${resolvedName}"`;
        }
      }

      if (isMesh && geom.mesh) {
        // Convert mesh UUID to mesh name for XML
        const meshXmlName = meshUuidToXmlName[geom.mesh] || sanitizeMjcfName(`mesh_${geom.mesh}`);
        xml += ` mesh="${meshXmlName}"`;
      }
      if (!isMesh && geomKind === "hfield" && exportGeom.heightField) {
        const hfieldName = String(exportGeom.heightField);
        const hfieldXmlName = hfieldNameToXmlName[hfieldName] || sanitizeMjcfName(hfieldName);
        xml += ` hfield="${hfieldXmlName}"`;
      }
      const hasFromTo =
        !isMesh && Array.isArray(exportGeom.fromto) && exportGeom.fromto.length >= 6;
      const emitFromTo = hasFromTo && (geomKind === "capsule" || geomKind === "cylinder");
      if (hasFromTo && !emitFromTo) {
        logger.warn(
          `[xmlGenerationActions] Geom type "${geomKind}" does not support fromto; attribute omitted.`
        );
      }
      if (!isMesh && geom.size?.length) {
        const expectedSizeCounts: Record<string, number> = {
          sphere: 1,
          capsule: emitFromTo ? 1 : 2,
          cylinder: emitFromTo ? 1 : 2,
          box: 3,
          ellipsoid: 3,
          plane: 3,
        };
        const expected = expectedSizeCounts[geomKind];
        // Primitives in the editor use a unified length-3 size vector (see DEFAULT_GEOM_SIZE_BY_KIND);
        // MuJoCo uses fewer components for sphere (1) and cylinder/capsule without fromto (2).
        // Truncation is intentional ? only warn when padding with zeros (likely incomplete data).
        if (expected !== undefined && geom.size.length < expected) {
          logger.warn(
            `[xmlGenerationActions] Geom "${geomKind}" expects ${expected} size value(s), got ${geom.size.length}; padding with zeros.`
          );
        }
        const sizeSlice =
          expected !== undefined
            ? (geom.size.slice(0, expected) as number[]).concat(
                Array(Math.max(0, expected - geom.size.length)).fill(0)
              )
            : geom.size;
        xml += ` size="${arrayToString(sizeSlice)}"`;
      }
      if (emitFromTo) {
        xml += ` fromto="${arrayToString(exportGeom.fromto!.slice(0, 6))}"`;
      }
      let geomPosForExport =
        Array.isArray(geom.position) && geom.position.length === 3
          ? geom.position.map((v: unknown) => Number(v) || 0)
          : [0, 0, 0];
      let geomQuatForExport =
        Array.isArray(geom.orientation) && geom.orientation.length === 4
          ? toMjcfQuatWxyz(geom.orientation)
          : [1, 0, 0, 0];

      // Bake MJCF mesh refs into geom transforms using v' = inv(refquat) * (v - refpos).
      if (isMesh && geom.mesh) {
        const meshMeta = (meshDefinitions[geom.mesh] || null) as MeshMetadataForExport | null;
        const refpos =
          Array.isArray(meshMeta?.referencePosition) && meshMeta.referencePosition.length === 3
            ? meshMeta.referencePosition.map((v: unknown) => Number(v) || 0)
            : [0, 0, 0];
        const refquat =
          Array.isArray(meshMeta?.referenceOrientation) &&
          meshMeta.referenceOrientation.length === 4
            ? toMjcfQuatWxyz(meshMeta.referenceOrientation)
            : [1, 0, 0, 0];
        const vertexOffsetInMeshFrame = quatRotateVec(quatConjugate(refquat), [
          -Number(refpos[0]),
          -Number(refpos[1]),
          -Number(refpos[2]),
        ]);
        const rotatedRefPos = quatRotateVec(geomQuatForExport, vertexOffsetInMeshFrame);
        geomPosForExport = [
          Number(geomPosForExport[0]) + Number(rotatedRefPos[0]),
          Number(geomPosForExport[1]) + Number(rotatedRefPos[1]),
          Number(geomPosForExport[2]) + Number(rotatedRefPos[2]),
        ];
        geomQuatForExport = quatMultiply(geomQuatForExport, quatConjugate(refquat));
      }

      if (!hasFromTo && geomPosForExport.some((value) => Math.abs(Number(value)) > 1e-12)) {
        xml += ` pos="${arrayToString(geomPosForExport)}"`;
      }
      const normalizedGeomQuat = toMjcfQuatWxyz(geomQuatForExport);
      if (
        !hasFromTo &&
        !(
          normalizedGeomQuat[0] === 1 &&
          normalizedGeomQuat[1] === 0 &&
          normalizedGeomQuat[2] === 0 &&
          normalizedGeomQuat[3] === 0
        )
      ) {
        xml += ` quat="${arrayToString(normalizedGeomQuat)}"`;
      }
      if (!geom.visualOnly) {
        const fc =
          Array.isArray(geom.frictionCoefficients) && geom.frictionCoefficients.length >= 3
            ? geom.frictionCoefficients
            : null;
        xml += ` friction="${arrayToString(fc || [0.8, 0.01, 0.0001])}"`;

        const srRaw =
          Array.isArray(geom.solverReference) && geom.solverReference.length >= 2
            ? [geom.solverReference[0], geom.solverReference[1]]
            : [0.02, 1];
        const sr0 = Number(srRaw[0]);
        const sr1 = Number(srRaw[1]);
        const sr = [Number.isFinite(sr0) ? sr0 : 0.02, Number.isFinite(sr1) ? sr1 : 1];
        xml += ` solref="${arrayToString(sr)}"`;

        const siDefaults = [0.9, 0.95, 0.001, 0.5, 2];
        const siRaw =
          Array.isArray(geom.solverImpedance) && geom.solverImpedance.length >= 3
            ? [
                geom.solverImpedance[0],
                geom.solverImpedance[1],
                geom.solverImpedance[2],
                geom.solverImpedance[3],
                geom.solverImpedance[4],
              ]
            : siDefaults;
        const si = siDefaults.map((def, i) => {
          const n = Number(siRaw[i]);
          return Number.isFinite(n) ? n : def;
        });
        xml += ` solimp="${arrayToString(si)}"`;
      }
      if (exportGeom.mass !== undefined && exportGeom.mass !== null) {
        const massVal = Number(exportGeom.mass);
        if (Number.isFinite(massVal) && massVal >= 0) {
          xml += ` mass="${formatMjcfNumber(massVal)}"`;
        } else {
          logger.warn(
            `[xmlGenerationActions] Geom mass "${exportGeom.mass}" is not a valid non-negative number; attribute omitted.`
          );
        }
      }
      if (exportGeom.density !== undefined && exportGeom.density !== null) {
        const densityVal = Number(exportGeom.density);
        if (Number.isFinite(densityVal) && densityVal > 0) {
          xml += ` density="${formatMjcfNumber(densityVal)}"`;
        } else {
          const bodyLabel = body?.name
            ? String(body.name)
            : body?.uuid
              ? String(body.uuid)
              : "(unnamed)";
          const geomLabel =
            geom?.name && String(geom.name).trim().length > 0
              ? String(geom.name)
              : geom?.uuid
                ? String(geom.uuid)
                : `${geomKind} geom`;
          addExportWarning(
            `Geom "${geomLabel}" on body "${bodyLabel}" has density "${String(exportGeom.density)}". Export omits density unless it is a positive number; set density > 0 or clear it.`
          );
        }
      }
      const contactCategory = (() => {
        if (geom.visualOnly === true) return 0;
        const value = Number(geom.contactCategory);
        return Number.isFinite(value) && value >= 0 && Number.isInteger(value) ? value : 1;
      })();
      const contactAffinity = (() => {
        if (geom.visualOnly === true) return 0;
        const value = Number(geom.contactAffinity);
        return Number.isFinite(value) && value >= 0 && Number.isInteger(value) ? value : 1;
      })();
      xml += ` contype="${contactCategory}"`;
      xml += ` conaffinity="${contactAffinity}"`;

      if (exportGeom.contactDimensions !== undefined && exportGeom.contactDimensions !== null) {
        const cd = Number(exportGeom.contactDimensions);
        if ([1, 3, 4, 6].includes(cd)) {
          xml += ` condim="${cd}"`;
        }
      }
      if (typeof exportGeom.user === "string" && exportGeom.user.trim().length > 0) {
        xml += ` user="${xmlEscapeAttr(exportGeom.user.trim())}"`;
      }
      xml += "/>\n";
    });

    // Legacy fallback removed: prefer explicit geoms; do not emit from body.meshes

    xml += emitBodySitesXml({
      body,
      indent,
      siteExportNameByUuid,
      materialUuidToName,
      helpers: cameraSiteXmlHelpers,
    });

    const mountedCameras = mountedCamerasByBodyUuid[String(body.uuid)] || [];
    xml += emitMountedCamerasXml({
      body,
      indent,
      mountedCameras,
      cameraExportNameByUuid,
      bodyUuidToXmlName,
      helpers: cameraSiteXmlHelpers,
    });

    const mountedLights = mountedLightsByBodyUuid[String(body.uuid)] || [];
    xml += emitMountedLightsXml({
      indent,
      mountedLights,
      lightExportNameByUuid,
      bodyUuidToXmlName,
      helpers: cameraSiteXmlHelpers,
    });

    body.children?.forEach((child) => {
      xml += generateBodyXml(child, `${indent}  `);
    });

    xml += `${indent}</body>\n`;
    return xml;
  };

  let worldbodyBodyXml = "";
  bodyTreeForExport.forEach((bodyNode) => {
    worldbodyBodyXml += generateBodyXml(bodyNode, "    ");
  });

  return { worldbodyBodyXml, generateBodyXml };
};
